<script setup lang="ts">
/**
 * Account side panel — slide-in 480px right drawer hosting three
 * sub-views (`main` / `iban` / `email`). The shell owns the scrim,
 * keyboard Esc, and view dispatch; sub-views own their forms and
 * API state.
 *
 * Mirrors the design bundle's `AccountPanel.jsx` shell + nested
 * `PanelHeader` component. The `.pp-panel` family lives in
 * `src/design-system/shell.css`.
 *
 * Phase 2 (portal-fe) mounts this organism inside `PortalShell.vue`
 * so it's available on every portal page; the `accountPanel` Pinia
 * store drives `isOpen` and `view`.
 */
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PortalSelfResponse } from '@/stores/portalSelf.store'
import AccountPanelEmail from './AccountPanelEmail.vue'
import AccountPanelIban from './AccountPanelIban.vue'
import AccountPanelMain from './AccountPanelMain.vue'

type View = 'main' | 'iban' | 'email'

const props = defineProps<{
  isOpen: boolean
  view: View
  partner: PortalSelfResponse | null
}>()

const emit = defineEmits<{
  close: []
  'update:view': [view: View]
  'partner-update': [partner: PortalSelfResponse]
  logout: []
}>()

const { t } = useI18n({ useScope: 'global' })

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.isOpen) emit('close')
}

onMounted(() => {
  document.addEventListener('keydown', onKey)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
})

// Re-attach the listener if the panel is reopened after the shell
// has been re-mounted (HMR safety — `onMounted` only fires once).
watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    // Defensive: removing first prevents duplicate handlers if HMR
    // caused the previous listener to leak.
    document.removeEventListener('keydown', onKey)
    document.addEventListener('keydown', onKey)
  },
)

function setView(v: View): void {
  emit('update:view', v)
}

function onPartnerUpdate(p: PortalSelfResponse): void {
  emit('partner-update', p)
}

function titleFor(v: View): string {
  return t(`portal.account_panel.titles.${v}`)
}
</script>

<template>
  <div :class="['pp-panel__scrim', { 'is-open': isOpen }]" @click="emit('close')" />
  <aside :class="['pp-panel', { 'is-open': isOpen }]" :aria-hidden="!isOpen">
    <header class="pp-panel__head">
      <button
        v-if="view !== 'main'"
        type="button"
        class="pp-panel__back"
        aria-label="Indietro"
        @click="setView('main')"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <h2 class="pp-panel__title">{{ titleFor(view) }}</h2>
      <button type="button" class="pp-panel__close" aria-label="Chiudi" @click="emit('close')">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </header>

    <div v-if="partner" class="pp-panel__body">
      <AccountPanelMain
        v-if="view === 'main'"
        :partner="partner"
        @go="(v) => setView(v)"
        @logout="emit('logout')"
      />
      <AccountPanelIban
        v-else-if="view === 'iban'"
        :partner="partner"
        @back="setView('main')"
        @done="onPartnerUpdate"
      />
      <AccountPanelEmail
        v-else
        :partner="partner"
        @back="setView('main')"
        @done="onPartnerUpdate"
      />
    </div>
  </aside>
</template>
